import { useId } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import type { ServiceOption } from '../data/services';
import { getIcon } from '../utils/iconMap';

type ServiceSelectorProps = {
  services: ServiceOption[];
  selectedServiceId: string;
  onChange: (service: ServiceOption) => void;
  error?: string;
  title?: string;
  helper?: string;
};

export function ServiceSelector({
  services,
  selectedServiceId,
  onChange,
  error,
  title = 'Como podemos ajudar?',
  helper = 'Selecione o serviço que você procura.'
}: ServiceSelectorProps) {
  const headingId = useId();
  const errorId = useId();

  return (
    <section className="service-selector" aria-labelledby={headingId}>
      <div className="service-selector__intro">
        <h3 id={headingId}>{title}</h3>
        <p>{helper}</p>
      </div>

      <div
        className="service-grid"
        role="radiogroup"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
      >
        {services.map((service, index) => {
          const Icon = getIcon(service.icon);
          const isSelected = service.id === selectedServiceId;

          return (
            <motion.button
              key={service.id}
              className={`service-card${isSelected ? ' service-card--selected' : ''}`}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onChange(service)}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0, scale: isSelected ? 1.015 : 1 }}
              transition={{ duration: 0.22, delay: index * 0.025, ease: 'easeOut' }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.985 }}
            >
              <span className="service-card__icon" aria-hidden="true">
                <Icon size={17} strokeWidth={1.9} />
              </span>

              <span className="service-card__text">
                <strong>{service.title}</strong>
                <small>{service.description}</small>
              </span>

              <motion.span
                className="service-card__check"
                aria-hidden="true"
                initial={false}
                animate={isSelected ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.72 }}
                transition={{ duration: 0.16, ease: 'easeOut' }}
              >
                <Check size={13} strokeWidth={2.4} />
              </motion.span>
            </motion.button>
          );
        })}
      </div>

      {error ? (
        <p className="service-selector__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
